import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { requestAccess } from '../../api';

// The landing page's Request access form. The "website" field is a trap:
// hidden from people and screen readers, filled in by bots, and the server
// quietly drops anything that arrives with it.

const TEAM_SIZES = [
  { value: '', label: 'Choose one' },
  { value: '1-5', label: '1 to 5 people' },
  { value: '6-20', label: '6 to 20 people' },
  { value: '21-50', label: '21 to 50 people' },
  { value: '51+', label: 'More than 50' },
];

const EMPTY = { name: '', email: '', company: '', role: '', team_size: '', message: '', website: '' };

export default function RequestAccessForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState(null);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await requestAccess(form);
      setSentTo({ name: form.name.trim().split(/\s+/)[0], email: form.email.trim() });
      setForm(EMPTY);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sentTo) {
    return (
      <div className="lp-form-done" role="status">
        <CheckCircle2 size={32} aria-hidden="true" />
        <h3>Thanks, {sentTo.name}.</h3>
        <p>Your request is in. We will reply to {sentTo.email} to set up your access.</p>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={submit}>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      <div className="lp-form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="ra-name">Name</label>
          <input id="ra-name" className="form-input" value={form.name} onChange={set('name')} required maxLength={100} autoComplete="name" />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="ra-email">Work email</label>
          <input id="ra-email" type="email" className="form-input" value={form.email} onChange={set('email')} required maxLength={200} autoComplete="email" />
        </div>
      </div>
      <div className="lp-form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="ra-company">Company</label>
          <input id="ra-company" className="form-input" value={form.company} onChange={set('company')} required maxLength={150} autoComplete="organization" />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="ra-role">Your role <span className="lp-optional">(optional)</span></label>
          <input id="ra-role" className="form-input" value={form.role} onChange={set('role')} maxLength={100} autoComplete="organization-title" />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ra-team">Sales team size <span className="lp-optional">(optional)</span></label>
        <select id="ra-team" className="form-input" value={form.team_size} onChange={set('team_size')}>
          {TEAM_SIZES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ra-message">What do you sell, and how? <span className="lp-optional">(optional)</span></label>
        <textarea id="ra-message" className="form-input" rows={3} value={form.message} onChange={set('message')} maxLength={2000} />
      </div>
      <div className="lp-trap" aria-hidden="true">
        <label htmlFor="ra-website">Website</label>
        <input id="ra-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
      </div>
      <button type="submit" className="btn btn-primary lp-form-submit" disabled={sending}>
        <Send size={18} aria-hidden="true" /> {sending ? 'Sending…' : 'Request access'}
      </button>
      <p className="lp-form-note">We use these details only to reply to you.</p>
    </form>
  );
}
